/**
 * Curated 24-photo Unsplash pool for the "Life at [University]" bento
 * grid, plus a deterministic per-university picker so every university
 * ships a distinct-looking set of 6 photos even before Google Places
 * gallery seeding lands.
 *
 * Rationale: the previous fallback was 6 hard-coded URLs, identical
 * across every course + every university, which read as "generic AI
 * slop" to families comparing multiple courses side-by-side. This
 * module gives us a stable, reproducible mapping — the same uni
 * always renders the same 6 photos (so users don't see them reshuffle
 * on refresh), but neighbouring unis get visibly different sets.
 *
 * When `universities.gallery_google_places` is populated by the
 * Google Places seed job, those real photos take precedence and this
 * pool becomes a fill-only fallback for the tail of unis without
 * Google Places coverage.
 */

// 6 unmistakably GENERIC study-life photo IDs — deliberately chosen
// to avoid recognisable landmarks. The previous set included the
// Bodleian Library and an Oxford gothic doorway which leaked onto
// every uni page without its own gallery (users saw Oxford landmarks
// on UCL, Imperial, LSE etc.). These replacements were re-inspected
// via image analysis Feb 2026 and confirmed to depict:
//   - two students sharing an open laptop (no landmark visible)
//   - a hand-written notebook + coffee mug (desk close-up)
//   - a bookshelf-lined library aisle (no signage, no identifier)
//   - highlighter marks on a printed page (macro shot)
//   - a stack of textbooks on a wooden desk
//   - back-of-head student in a plain lecture-hall row (no wall art)
// Real per-uni photos from the Places seed job take precedence when
// available; this pool is a top-up only when Places returns < 6.
const CAMPUS_PHOTO_POOL = [
  "1523240795612-9a054b0db644",  // two students, shared laptop, plain background
  "1517842645767-c639042777db",  // handwritten notes + coffee (verified)
  "1521587760476-6c12a4b040da",  // library aisle, no identifiers
  "1497633762265-9d179a990aa6",  // stack of textbooks on desk
  "1522202176988-66273c2fd55f",  // student group at desk, plain wall
  "1509062522246-3755977927d7",  // notebook + laptop close-up
  "1541178735493-479c1a27ed24",  // student at laptop, plain classroom
  "1513258496099-48168024aec0",  // student with headphones at laptop
  "1515378960530-7c0da6231fb1",  // hands typing on laptop, wooden desk
  "1491975474562-1f4e30bc9468",  // student at laptop by a window
  "1596347909571-2f249cfdbb0a",  // laptop on wooden study desk
  "1764096534662-a194a348c4a0",  // late-night desk, laptop + papers
  "1784065654365-e92dd2c95bf3",  // notebook, pencil + coffee, white desk
  "1787675907938-81e0bc921100",  // spiral notebook on table, group study
  "1776178320206-f42b9a9cf996",  // students at tables in a plain classroom
  "1604866830893-c13cafa515d5",  // library aisle, no signage
  "1513475382585-d06e58bcb0e0",  // hand picking a book off a shelf
  "1614849963640-9cc74b2a826f",  // hand reaching for books on a shelf
];

const URL_SUFFIX = "?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

/**
 * djb2 — tiny deterministic string hash. Same input always yields
 * same 32-bit int; distribution is good enough for our 24-photo pool
 * that we don't need a heavyweight algorithm.
 */
function hashString(s) {
  let h = 5381;
  const str = String(s || "");
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 33) ^ str.charCodeAt(i);
  }
  return h >>> 0; // coerce to unsigned 32-bit
}

/**
 * Pick `count` photos from the pool deterministically for a given
 * university identifier. Uses a hash-seeded stride walk through the
 * pool so no two adjacent picks are the same photo, and different
 * unis end up with visibly different starting points.
 *
 * Feb 2026 fix: the previous version could infinite-loop when the
 * hashed stride shared a common factor with pool.length (e.g. stride
 * 3 or 6 on a 6-item pool cycled through only 2 or 1 indices, never
 * filling `count`). We now fall back to a +1 advance whenever the
 * stride lands on an already-seen index, which guarantees we always
 * make progress and exit within pool.length iterations.
 */
export function pickPhotosForUni(universityId, count = 6) {
  const pool = CAMPUS_PHOTO_POOL;
  if (!pool.length) return [];
  const seed = hashString(universityId);
  const start = seed % pool.length;
  const stride = (seed % 5) + 3; // 3..7 — coprime-ish stride so all pool slots reachable
  const picks = [];
  const seen = new Set();
  let idx = start;
  while (picks.length < count && seen.size < pool.length) {
    if (!seen.has(idx)) {
      picks.push(`https://images.unsplash.com/photo-${pool[idx]}${URL_SUFFIX}`);
      seen.add(idx);
      idx = (idx + stride) % pool.length;
    } else {
      // Stride landed on an already-picked index — advance by 1 so we
      // always make progress toward covering the whole pool. Without
      // this, non-coprime stride/pool combos (e.g. stride 3 on a
      // 6-item pool) spin forever.
      idx = (idx + 1) % pool.length;
    }
  }
  return picks;
}

/**
 * Exported for tests and debugging — total pool size caller can rely on.
 */
export const CAMPUS_PHOTO_POOL_SIZE = CAMPUS_PHOTO_POOL.length;
